import { LineChart } from 'echarts/charts'
import { DataZoomComponent, GridComponent, TooltipComponent } from 'echarts/components'
import * as echarts from 'echarts/core'
import type { ECharts } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import type { PropType } from 'vue'
import './DepthMap.scss'

echarts.use([LineChart, TooltipComponent, GridComponent, CanvasRenderer, DataZoomComponent])

export const DepthChart = defineComponent({
  name: 'DepthMap',
  props: {
    buyList: {
      type: Array as PropType<SocketResponse.HandicapData[]>,
      default: () => [],
    },
    sellList: {
      type: Array as PropType<SocketResponse.HandicapData[]>,
      default: () => [],
    },
    priceText: {
      type: String,
      default: () => '',
    },
    numText: {
      type: String,
      default: () => '',
    },
    decimal: {
      type: Number,
      default: () => 0,
    },
    family: {
      type: String,
      default: () => '',
    },
    lineColor: {
      type: String,
      default: () => '',
    },
    downColor: {
      type: String,
      default: () => '',
    },
    upColor: {
      type: String,
      default: () => '',
    },
  },
  setup(props) {
    let chart: ECharts | null = null
    const chartRef = ref<HTMLElement | null>(null)

    function buildDepthData(list: SocketResponse.HandicapData[], type: 'buy' | 'sell'): number[][] {
      const sorted = [...list].sort((a, b) => (type === 'buy' ? Number(b[0]) - Number(a[0]) : Number(a[0]) - Number(b[0])))

      const result: number[][] = []
      let cumulative = 0

      for (const [price, amount] of sorted) {
        cumulative += +amount
        result.push([+price, +cumulative])
      }

      return result.sort((a, b) => +a[0] - +b[0])
    }

    function resolveCssVar(color: string) {
      if (color.startsWith('var(')) {
        return getComputedStyle(document.documentElement)
          .getPropertyValue(color.replace(/var\(|\)/g, ''))
          .trim()
      }
      return color
    }

    const renderChart = () => {
      if (!chartRef.value) return
      if (!chart) {
        chart = echarts.init(chartRef.value)
      }

      const buyData = buildDepthData(props.buyList, 'buy')
      const sellData = buildDepthData(props.sellList, 'sell')

      chart.setOption({
        animation: false,
        tooltip: {
          trigger: 'axis',
          confine: true,
          formatter: (params: { data: number[] }[]) => {
            const data = params[0]?.data
            return `${props.priceText}：${data?.[0]?.formatNumberWithCommas()}<br/>${props.numText}：${data?.[1]?.toStringAsFixed(props.decimal)}`
          },
          textStyle: {
            fontFamily: props.family,
          },
        },
        grid: {
          top: 20,
          bottom: 30,
          left: 10,
          right: 10,
        },
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: 0,
          },
          {
            type: 'slider',
            xAxisIndex: 0,
            show: false,
          },
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          axisLabel: {
            showMinLabel: false,
            formatter: (val: number) => `${val?.formatNumberWithCommas()}`,
            fontFamily: props.family,
            textStyle: {
              color: 'var(--colors-text-base)',
            },
          },
          min: 'dataMin',
          max: 'dataMax',
          axisLine: {
            lineStyle: {
              color: resolveCssVar(props.lineColor),
            },
          },
        },
        yAxis: {
          type: 'value',
          position: 'right',
          splitNumber: 4,
          axisTick: { inside: true },
          axisLabel: {
            inside: true,
            showMinLabel: false,
            fontFamily: props.family,
            textStyle: {
              color: 'var(--colors-text-base)',
            },
          },
          splitLine: { show: false },
        },
        series: [
          {
            name: '',
            data: buyData,
            type: 'line',
            step: 'end',
            symbol: 'circle',
            showSymbol: false,
            symbolSize: 10,
            itemStyle: {
              color: resolveCssVar(props.upColor),
              borderColor: resolveCssVar(props.upColor),
              shadowColor: 'rgba(0, 0, 0, 0.5)',
              shadowBlur: 10,
              fontFamily: props.family,
            },
            label: {
              show: true,
              position: 'right',
              distance: 10,
              padding: 10,
              fontSize: 12,
              color: '#fff',
              backgroundColor: 'rgba(0, 0, 0, .6)',
              formatter: (params: { data: number[] }) =>
                `${props.priceText} ：${params.data[0]?.formatNumberWithCommas()}\n${props.numText} ：${params.data[1]?.toStringAsFixed(props.decimal)}`,
              rich: {
                a: {
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 'bold',
                  lineHeight: 20,
                },
              },
            },
            lineStyle: { color: resolveCssVar(props.upColor) },
            areaStyle: { color: resolveCssVar(props.upColor), opacity: 0.2 },
          },
          {
            name: '',
            data: sellData,
            type: 'line',
            step: 'end',
            symbol: 'circle',
            showSymbol: false,
            symbolSize: 10,
            itemStyle: {
              color: props.downColor,
              borderColor: props.downColor,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
              shadowBlur: 10,
              fontFamily: props.family,
            },
            label: {
              show: true,
              position: 'left',
              distance: 10,
              padding: 10,
              fontSize: 12,
              color: '#fff',
              backgroundColor: 'rgba(0, 0, 0, .6)',
              formatter: (params: { data: number[] }) =>
                `${props.priceText} ：${params.data[0]?.formatNumberWithCommas()}\n${props.numText} ：${params.data[1]?.toStringAsFixed(props.decimal)}`,
              rich: {
                a: {
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 'bold',
                  lineHeight: 20,
                },
              },
            },
            lineStyle: { color: resolveCssVar(props.downColor) },
            areaStyle: { color: resolveCssVar(props.downColor), opacity: 0.2 },
          },
        ],
      })
    }

    function handleResize() {
      chart?.resize()
    }

    watch(
      () => [props.buyList, props.sellList],
      (value) => {
        chart?.showLoading()
        if (chart && value[0].length && value[1].length) {
          renderChart()
          chart?.hideLoading()
        }
      },
      { deep: true, immediate: true },
    )

    onMounted(async () => {
      await nextTick(() => {
        renderChart()
      })
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      chart?.dispose()
      window.removeEventListener('resize', handleResize)
    })

    return () => (
      <div class="depth-map-wrap">
        <div ref={chartRef} class="chart" />
      </div>
    )
  },
})
